import { useEffect, useRef } from "react";
import apiClient from "../utils/apiClient";
import { createMediaBytesMeter } from "../utils/mediaBytes";

/**
 * useVideoWatchTracker
 *
 * Tracks how much of a video a student has *actually watched* and persists it
 * as cumulative watch time — driving the real progress rings in the recordings
 * lists (BE: GET /videos/batch/:id/view-status computes percent from this).
 *
 * Design goals:
 *  • Real coverage, not seek-gaming — only small forward `timeupdate` deltas are
 *    counted. Dragging the scrubber produces a large jump that is ignored, so
 *    "drag to the end + watch 1 min" accrues ~1 min, never 100%.
 *  • Minimal server load — NO per-second heartbeat. We POST only on pause /
 *    tab-hidden / unmount, throttled to MIN_SEND_DELTA of new watch time.
 *  • Cumulative & monotonic — we send `baseline + sessionWatched`; the BE keeps
 *    max(existing, sent) capped at the video length, so it never regresses.
 *  • …but `lastPosition` is NOT cumulative. It is the playhead where the student
 *    stopped, and it overwrites server-side, because that is the only number a
 *    "continue watching" resume can seek to. Resuming from watchDuration (what
 *    this app used to do) drops a student who rewound further ahead than they
 *    ever actually reached.
 *  • It also reports BANDWIDTH. Video is served straight from the CDN, so the
 *    API server never sees a byte of it and the browser is the only witness —
 *    see utils/mediaBytes.js. `bytes` is a DELTA since the last successful
 *    flush, not a running total, because the server adds it to a per-student
 *    ledger; sending a total would count the same bytes on every flush.
 *    `bytesSource: 'timing'` means the number is exact (cache hits included as
 *    a real zero); when the browser redacts it cross-origin the field is
 *    omitted entirely and the server derives a figure from watch coverage
 *    instead. Omitted is not zero, and the two must never be conflated.
 *
 * @param {object}   args
 * @param {React.RefObject<HTMLVideoElement>} args.videoRef
 * @param {string}   args.videoId        Recording _id (POST target).
 * @param {number}   args.durationMin    Video length in minutes (for the cap).
 * @param {number}   args.baselineSec    Previously-stored watch seconds.
 * @param {boolean}  args.active         Only track while the player is shown.
 * @param {function} args.onProgress     (watchDuration, totalSec, lastPosition) => void
 *                                       — lets the parent refresh its local view-status.
 */
const MIN_SEND_DELTA = 3; // seconds of new watch time before another POST
const MAX_GAP = 2;        // a timeupdate jump larger than this (×rate) is a seek

export default function useVideoWatchTracker({
  videoRef,
  videoId,
  durationMin = 0,
  baselineSec = 0,
  active = true,
  onProgress,
}) {
  const sessionSec = useRef(0); // real seconds watched THIS session
  const lastTime = useRef(0);
  const lastSent = useRef(0); // sessionSec value at last successful POST
  const flushRef = useRef(null); // current video's flush (for the visibility effect)

  // Keep latest baseline / callback in refs, synced via effects so that — on a
  // video switch — the *previous* video's cleanup flush still reads the
  // previous baseline (cleanups run before these effect bodies re-apply).
  const baselineRef = useRef(baselineSec);
  const onProgressRef = useRef(onProgress);
  useEffect(() => { baselineRef.current = baselineSec; }, [baselineSec]);
  useEffect(() => { onProgressRef.current = onProgress; }, [onProgress]);

  // ── Attach media listeners; each effect owns its video's flush ──
  useEffect(() => {
    sessionSec.current = 0;
    lastTime.current = 0;
    lastSent.current = 0;

    const id = videoId;
    const totalSec = durationMin > 0 ? Math.round(durationMin * 60) : 0;
    const meter = createMediaBytesMeter();

    const flush = (force = false) => {
      if (!id) return;
      const sess = Math.round(sessionSec.current);
      if (sess <= 0 || sess === lastSent.current) return;
      if (!force && sess - lastSent.current < MIN_SEND_DELTA) return;

      lastSent.current = sess;
      let watchDuration = Math.round(baselineRef.current + sess);
      if (totalSec > 0) watchDuration = Math.min(watchDuration, totalSec);

      // Where the playhead is right now — read at flush time, because the
      // element is still mounted here even on the unmount path (the cleanup
      // runs before React detaches it).
      let lastPosition = Math.max(0, Math.round(videoRef.current?.currentTime || 0));
      if (totalSec > 0) lastPosition = Math.min(lastPosition, totalSec);

      // Bytes actually transferred since the last flush. Taken BEFORE the POST
      // so the reading matches the watch time it travels with.
      const { bytes, source } = meter.takeDelta();
      const payload = { watchDuration, lastPosition };
      if (source === "timing") {
        payload.bytes = bytes;
        payload.bytesSource = source;
      }

      apiClient
        .post(`/videos/${id}/mark-viewed`, payload)
        .catch(() => {
          // The watch time self-heals — it is absolute and the next flush
          // resends it. The byte delta does not, so hand it back to the meter
          // rather than losing it to a dropped request.
          if (source === "timing" && bytes > 0) meter.credit(bytes);
        });
      onProgressRef.current?.(watchDuration, totalSec, lastPosition);
    };
    flushRef.current = flush;

    const v = videoRef.current;
    if (!v || !id || !active) {
      meter.stop();
      return undefined;
    }

    // `currentSrc` is what the element actually fetched — `src` may still be
    // the pre-resolution value, and the meter matches entries by exact url.
    const pointMeter = () => meter.watch(v.currentSrc);
    pointMeter();

    const onTime = () => {
      // Don't count while paused or mid-seek; just re-anchor the cursor.
      if (v.paused || v.seeking) {
        lastTime.current = v.currentTime;
        return;
      }
      const t = v.currentTime;
      const dt = t - lastTime.current;
      const maxStep = MAX_GAP * (v.playbackRate || 1) + 0.5;
      // Only small forward deltas are real playback; large jumps = scrub.
      if (dt > 0 && dt <= maxStep) sessionSec.current += dt;
      lastTime.current = t;
    };
    const resync = () => { lastTime.current = v.currentTime; };
    const softFlush = () => flush(false);

    v.addEventListener("timeupdate", onTime);
    v.addEventListener("seeking", resync);
    v.addEventListener("seeked", resync);
    v.addEventListener("pause", softFlush);
    v.addEventListener("ended", softFlush);
    v.addEventListener("loadstart", pointMeter);

    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("seeking", resync);
      v.removeEventListener("seeked", resync);
      v.removeEventListener("pause", softFlush);
      v.removeEventListener("ended", softFlush);
      v.removeEventListener("loadstart", pointMeter);
      flush(true); // final flush on video switch / unmount
      meter.stop();
    };
  }, [videoId, active, videoRef, durationMin]);

  // ── Flush when the tab is hidden (covers most tab-close / switch cases) ──
  useEffect(() => {
    const onHide = () => { if (document.hidden) flushRef.current?.(false); };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, []);
}
